import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { collection, doc, getDoc, getDocs, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { WebsiteCmsService } from '../../services/websiteCmsService';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { UI, denseInputSx, primaryButtonSx, textButtonSx } from './settingsUi';
import { AUTO_REPLY_LANGS } from '../../utils/whatsappAutoReplyFlows';

type Tool = 'constructionComposer' | 'possessionComposer' | 'documentsChecklist' | 'priceInDrawer' | 'demandDueDate' | 'launchComposer' | 'festivalComposer' | 'collectionComposer' | 'coldLeadInfo' | 'birthdayInfo';

const useCrmProjects = () => {
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    getDocs(collection(db, 'projects'))
      .then((snap) =>
        setProjects(
          snap.docs
            .map((d) => ({ id: d.id, name: String((d.data() as any).name || d.id) }))
            .sort((a, b) => a.name.localeCompare(b.name))
        )
      )
      .catch(() => setProjects([]));
  }, []);
  return projects;
};

const Panel = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ mt: 2.5, border: `1px solid ${UI.line}`, borderRadius: 1.5 }}>
    <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${UI.line}`, bgcolor: UI.canvas }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: UI.ink }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 1.5 }}>{children}</Box>
  </Box>
);

const ProjectSelect = ({ value, onChange }: { value: string; onChange: (id: string) => void }) => {
  const projects = useCrmProjects();
  return (
    <TextField select size="small" label="Project" value={value} onChange={(e) => onChange(e.target.value)} sx={{ ...denseInputSx, minWidth: 220 }}>
      {projects.map((p) => (
        <MenuItem key={p.id} value={p.id} sx={{ fontSize: 13 }}>
          {p.name}
        </MenuItem>
      ))}
    </TextField>
  );
};

const BroadcastComposer = ({ kind }: { kind: 'construction' | 'possession' }) => {
  const [projectId, setProjectId] = useState('');
  const [update, setUpdate] = useState('');
  const [possession, setPossession] = useState('');
  const [readyDate, setReadyDate] = useState('');
  const [count, setCount] = useState<{ recipients: number; noPhone: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => setCount(null), [projectId]);

  const call = async (dryRun: boolean) => {
    setBusy(true);
    setMessage(null);
    try {
      const res: any = await httpsCallable(functions, 'whatsappProjectBroadcast')({ kind, projectId, update, possession, readyDate, dryRun });
      if (dryRun) {
        setCount({ recipients: Number(res.data.recipients || 0), noPhone: Number(res.data.noPhone || 0) });
      } else {
        const counts = (res.data.counts || {}) as Record<string, number>;
        const sent = counts.sent || 0;
        const other = Object.entries(counts).filter(([k]) => k !== 'sent').map(([k, v]) => `${k}: ${v}`).join(', ');
        setMessage({ type: sent ? 'success' : 'info', text: `Sent to ${sent} buyer${sent === 1 ? '' : 's'}.${other ? ` Skipped: ${other}.` : ''}` });
        setCount(null);
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: String(err?.message || err) });
    } finally {
      setBusy(false);
    }
  };

  const ready = !!projectId && (kind === 'construction' ? !!update.trim() && !!possession.trim() : !!readyDate);

  return (
    <Panel title={kind === 'construction' ? 'Send a construction update' : 'Announce possession'}>
      <Stack gap={1.25}>
        <ProjectSelect value={projectId} onChange={setProjectId} />
        {kind === 'construction' ? (
          <>
            <TextField
              size="small"
              label="Update"
              placeholder="slab work of the 9th floor is complete"
              value={update}
              onChange={(e) => setUpdate(e.target.value.replace(/\n/g, ' '))}
              inputProps={{ maxLength: 300 }}
              helperText="One sentence. It is placed after: construction update for <project>:"
              sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
            />
            <TextField size="small" label="Expected possession" placeholder="December 2026" value={possession} onChange={(e) => setPossession(e.target.value)} sx={denseInputSx} />
          </>
        ) : (
          <TextField
            size="small"
            type="date"
            label="Ready for possession from"
            value={readyDate}
            onChange={(e) => setReadyDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...denseInputSx, width: 220 }}
          />
        )}
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Button size="small" variant="outlined" disabled={!projectId || busy} onClick={() => call(true)} sx={textButtonSx}>
            Check recipients
          </Button>
          {count && (
            <>
              <Typography sx={{ fontSize: 12.5, color: UI.muted }}>
                {count.recipients} buyer{count.recipients === 1 ? '' : 's'}
                {count.noPhone ? ` · ${count.noPhone} without a mobile number` : ''}
              </Typography>
              <Button size="small" variant="contained" disableElevation disabled={!ready || busy || !count.recipients} onClick={() => call(false)} sx={primaryButtonSx}>
                {busy ? 'Sending…' : `Send to ${count.recipients}`}
              </Button>
            </>
          )}
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: UI.muted }}>
          Goes to the primary customer of every booked or sold unit. Respects the master switch, test numbers, opt-outs and this automation's switch.
        </Typography>
        {message && <Alert severity={message.type} sx={{ fontSize: 12.5, py: 0 }}>{message.text}</Alert>}
      </Stack>
    </Panel>
  );
};

const DocumentsChecklistTool = () => {
  const { currentUser } = useAuth();
  const [projectId, setProjectId] = useState('');
  const [texts, setTexts] = useState<Record<string, string>>({ en: '', gu: '', hi: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setMessage(null);
    if (!projectId) return;
    getDoc(doc(db, 'whatsappSettings', 'projectMessages'))
      .then((snap) => {
        const docs = (snap.data() as any)?.documents?.[projectId] || {};
        setTexts({ en: docs.en || '', gu: docs.gu || '', hi: docs.hi || '' });
      })
      .catch(() => undefined);
  }, [projectId]);

  const save = async () => {
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'whatsappSettings', 'projectMessages'),
        { documents: { [projectId]: texts }, updatedAt: serverTimestamp(), updatedByEmail: currentUser?.email || null },
        { merge: true }
      );
      setMessage('Saved.');
    } catch (err: any) {
      setMessage(`Save failed: ${err?.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Documents checklist per project">
      <Stack gap={1.25}>
        <ProjectSelect value={projectId} onChange={setProjectId} />
        {projectId && (
          <>
            {AUTO_REPLY_LANGS.map(({ key, label }) => (
              <TextField
                key={key}
                size="small"
                label={label}
                placeholder={key === 'en' ? 'PAN card, Aadhaar card and 2 passport-size photos of each applicant' : 'Empty = default text'}
                value={texts[key]}
                onChange={(e) => setTexts((prev) => ({ ...prev, [key]: e.target.value.replace(/\n/g, ' ') }))}
                inputProps={{ maxLength: 300 }}
                sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
              />
            ))}
            <Stack direction="row" alignItems="center" gap={1}>
              <Button size="small" variant="contained" disableElevation disabled={saving} onClick={save} sx={primaryButtonSx}>
                {saving ? 'Saving…' : 'Save checklist'}
              </Button>
              {message && <Typography sx={{ fontSize: 12, color: UI.muted }}>{message}</Typography>}
            </Stack>
          </>
        )}
        <Typography sx={{ fontSize: 11.5, color: UI.muted }}>
          Placed after "please share:" in the message. Empty uses the default checklist.
        </Typography>
      </Stack>
    </Panel>
  );
};

const AUDIENCES = [
  { value: 'openLeads', label: 'Open leads' },
  { value: 'projectLeads', label: 'Open leads of one project' },
  { value: 'customers', label: 'Buyers (booked / sold units)' },
  { value: 'customersAndLeads', label: 'Buyers and open leads' },
];

const tomorrowYmd = () => {
  const d = new Date(Date.now() + 86400000);
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

const MarketingComposer = ({ kind }: { kind: 'newLaunch' | 'festival' }) => {
  const [audience, setAudience] = useState(kind === 'festival' ? 'customersAndLeads' : 'openLeads');
  const [projectId, setProjectId] = useState('');
  const [activeDays, setActiveDays] = useState(180);
  const [cmsProjects, setCmsProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [cmsProjectId, setCmsProjectId] = useState('');
  const [launchName, setLaunchName] = useState('');
  const [area, setArea] = useState('');
  const [highlight, setHighlight] = useState('');
  const [festival, setFestival] = useState('');
  const [message, setMessage] = useState('');
  const [when, setWhen] = useState<'now' | 'schedule'>(kind === 'festival' ? 'schedule' : 'now');
  const [scheduleYmd, setScheduleYmd] = useState(tomorrowYmd());
  const [count, setCount] = useState<Record<string, number> | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    if (kind !== 'newLaunch') return;
    WebsiteCmsService.getAllProjects()
      .then((list) => setCmsProjects(list.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => setCmsProjects([]));
  }, [kind]);

  useEffect(() => setCount(null), [audience, projectId, activeDays]);

  const payload = (dryRun: boolean) => ({
    kind,
    audience: { type: audience, projectId: audience === 'projectLeads' ? projectId : undefined, activeDays },
    cmsProjectId,
    launchName,
    area,
    highlight,
    festival,
    message,
    scheduleYmd: when === 'schedule' ? scheduleYmd : undefined,
    dryRun,
  });

  const call = async (dryRun: boolean) => {
    setBusy(true);
    setNotice(null);
    try {
      const res: any = await httpsCallable(functions, 'whatsappMarketingBroadcast')(payload(dryRun));
      if (dryRun) setCount(res.data as Record<string, number>);
      else if (res.data?.scheduled) {
        setNotice({ type: 'success', text: `Scheduled for 10 AM on ${scheduleYmd}.` });
        setCount(null);
      } else {
        const counts = (res.data?.counts || {}) as Record<string, number>;
        const other = Object.entries(counts).filter(([k]) => k !== 'sent').map(([k, v]) => `${k}: ${v}`).join(', ');
        setNotice({ type: 'success', text: `Sent to ${counts.sent || 0}.${other ? ` Skipped: ${other}.` : ''}` });
        setCount(null);
      }
    } catch (err: any) {
      setNotice({ type: 'error', text: String(err?.message || err) });
    } finally {
      setBusy(false);
    }
  };

  const contentReady = kind === 'newLaunch' ? !!cmsProjectId && !!highlight.trim() : !!festival.trim() && !!message.trim();
  const audienceReady = audience !== 'projectLeads' || !!projectId;

  return (
    <Panel title={kind === 'newLaunch' ? 'Announce a launch' : 'Send a festival greeting'}>
      <Stack gap={1.25}>
        {kind === 'newLaunch' ? (
          <>
            <TextField select size="small" label="Website project (for buttons)" value={cmsProjectId} onChange={(e) => setCmsProjectId(e.target.value)} sx={{ ...denseInputSx, minWidth: 240 }}>
              {cmsProjects.map((p) => (
                <MenuItem key={p.id} value={p.id} sx={{ fontSize: 13 }}>{p.name}</MenuItem>
              ))}
            </TextField>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <TextField size="small" label="Launch name" placeholder="Swarna Lagom Phase 2" value={launchName} onChange={(e) => setLaunchName(e.target.value)} sx={{ ...denseInputSx, flex: 1, minWidth: 180 }} />
              <TextField size="small" label="Area" placeholder="Vanakla, Surat" value={area} onChange={(e) => setArea(e.target.value)} sx={{ ...denseInputSx, flex: 1, minWidth: 160 }} />
            </Stack>
            <TextField size="small" label="Highlight" placeholder="2 BHK homes and retail shops" value={highlight} onChange={(e) => setHighlight(e.target.value.replace(/\n/g, ' '))} inputProps={{ maxLength: 120 }} sx={denseInputSx} />
          </>
        ) : (
          <>
            <TextField size="small" label="Festival" placeholder="Diwali" value={festival} onChange={(e) => setFestival(e.target.value)} sx={{ ...denseInputSx, maxWidth: 260 }} />
            <TextField size="small" label="Message" placeholder="May your new home bring you joy." value={message} onChange={(e) => setMessage(e.target.value.replace(/\n/g, ' '))} inputProps={{ maxLength: 200 }} sx={denseInputSx} />
          </>
        )}

        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <TextField select size="small" label="Audience" value={audience} onChange={(e) => setAudience(e.target.value)} sx={{ ...denseInputSx, minWidth: 240 }}>
            {AUDIENCES.map((a) => (
              <MenuItem key={a.value} value={a.value} sx={{ fontSize: 13 }}>{a.label}</MenuItem>
            ))}
          </TextField>
          {audience === 'projectLeads' && <ProjectSelect value={projectId} onChange={setProjectId} />}
          {audience !== 'customers' && (
            <TextField
              select
              size="small"
              label="Leads active in"
              value={activeDays}
              onChange={(e) => setActiveDays(Number(e.target.value))}
              sx={{ ...denseInputSx, width: 150 }}
            >
              {[30, 90, 180, 365].map((d) => (
                <MenuItem key={d} value={d} sx={{ fontSize: 13 }}>last {d} days</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          {(['now', 'schedule'] as const).map((w) => (
            <Box
              key={w}
              component="button"
              type="button"
              onClick={() => setWhen(w)}
              sx={{
                fontFamily: UI.body,
                fontSize: 12.5,
                border: `1px solid ${when === w ? UI.gold : UI.line}`,
                bgcolor: when === w ? UI.goldTint : 'transparent',
                color: when === w ? UI.gold : UI.ink,
                borderRadius: 1.25,
                px: 1.25,
                height: 28,
                cursor: 'pointer',
              }}
            >
              {w === 'now' ? 'Send now' : 'Schedule (10 AM)'}
            </Box>
          ))}
          {when === 'schedule' && (
            <TextField size="small" type="date" value={scheduleYmd} onChange={(e) => setScheduleYmd(e.target.value)} inputProps={{ min: tomorrowYmd() }} sx={{ ...denseInputSx, width: 160 }} />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Button size="small" variant="outlined" disabled={!audienceReady || busy} onClick={() => call(true)} sx={textButtonSx}>
            Check recipients
          </Button>
          {count && (
            <>
              <Typography sx={{ fontSize: 12.5, color: UI.muted }}>
                {count.recipients} in audience · about {count.willSend} will get it
                {count.optedOut ? ` · ${count.optedOut} opted out` : ''}
                {count.recentMarketing ? ` · ${count.recentMarketing} had marketing in the last 3 days` : ''}
              </Typography>
              <Button size="small" variant="contained" disableElevation disabled={!contentReady || busy || !count.willSend} onClick={() => call(false)} sx={primaryButtonSx}>
                {busy ? 'Working…' : when === 'schedule' ? `Schedule for ${scheduleYmd}` : `Send to ${count.willSend}`}
              </Button>
            </>
          )}
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: UI.muted }}>
          Marketing rules: 9 AM to 9 PM only, one marketing message per person per 3 days, up to 1,000 per send. Closed, lost and opted-out contacts are left out.
        </Typography>
        {notice && <Alert severity={notice.type} sx={{ fontSize: 12.5, py: 0 }}>{notice.text}</Alert>}
        <ScheduledList kind={kind} />
      </Stack>
    </Panel>
  );
};

const parseCollectionRecipients = (value: string) => {
  const recipients: Array<{ name: string; phone10: string; language: 'en' | 'gu' | 'hi' }> = [];
  const errors: string[] = [];
  const seen = new Set<string>();
  value.split(/\r?\n/).forEach((line, index) => {
    if (!line.trim()) return;
    const fields = line.split('|').map((part) => part.trim());
    const digits = (fields[1] || '').replace(/\D/g, '');
    const language = (fields[2] || 'en').toLowerCase();
    if (fields.length > 3 || !fields[0] || !/^(?:91)?\d{10}$/.test(digits) || !['en', 'gu', 'hi'].includes(language)) {
      errors.push(`Line ${index + 1}: use Name | 10-digit mobile | en/gu/hi.`);
      return;
    }
    const phone10 = digits.slice(-10);
    if (seen.has(phone10)) return;
    seen.add(phone10);
    recipients.push({ name: fields[0], phone10, language: language as 'en' | 'gu' | 'hi' });
  });
  return { recipients, errors };
};

const CollectionComposer = () => {
  const [recipientText, setRecipientText] = useState('');
  const [items, setItems] = useState({ en: '', gu: '', hi: '' });
  const [collectionDate, setCollectionDate] = useState('');
  const [fromTime, setFromTime] = useState('10:00');
  const [toTime, setToTime] = useState('18:00');
  const [address, setAddress] = useState('');
  const [mapQuery, setMapQuery] = useState('');
  const [when, setWhen] = useState<'now' | 'schedule'>('now');
  const [scheduleYmd, setScheduleYmd] = useState(tomorrowYmd());
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [count, setCount] = useState<Record<string, number> | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const parsed = parseCollectionRecipients(recipientText);
  const previewRecipient = parsed.recipients[0];
  const previewLang = previewRecipient?.language || 'en';
  const previewLocale = { en: 'en-IN', gu: 'gu-IN', hi: 'hi-IN' }[previewLang];
  const previewDate = collectionDate ? new Date(`${collectionDate}T12:00:00+05:30`).toLocaleDateString(previewLocale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—';
  const previewTime = (value: string) => new Date(`2000-01-01T${value}:00+05:30`).toLocaleTimeString(previewLocale, { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  const previewLines = previewLang === 'gu'
    ? [`નમસ્તે ${previewRecipient?.name || '—'} 👋`, `હવે તમે અમારી પાસેથી ${items.gu || '—'} મેળવી શકો છો.`, `કૃપા કરીને ${previewDate}ના રોજ ${previewTime(fromTime)}–${previewTime(toTime)} દરમિયાન મુલાકાત લો.`, `📍 મેળવવાનું સ્થળ: ${address || '—'}. નકશો જોવા નીચે દિશા જુઓ દબાવો.`]
    : previewLang === 'hi'
      ? [`नमस्ते ${previewRecipient?.name || '—'} 👋`, `अब आप हमसे ${items.hi || '—'} प्राप्त कर सकते हैं।`, `कृपया ${previewDate} को ${previewTime(fromTime)}–${previewTime(toTime)} के बीच आएँ।`, `📍 प्राप्त करने का स्थान: ${address || '—'}। नक्शे के लिए नीचे दिशा देखें दबाएँ।`]
      : [`Hello ${previewRecipient?.name || '—'} 👋`, `You can now collect ${items.en || '—'} from us.`, `Please visit us on ${previewDate}, between ${previewTime(fromTime)}–${previewTime(toTime)}.`, `📍 Collection point: ${address || '—'}. Tap Get directions below for the map.`];
  const ready = parsed.recipients.length > 0 && parsed.recipients.length <= 1000 && !parsed.errors.length &&
    !!items.en.trim() && !!items.gu.trim() && !!items.hi.trim() &&
    !!collectionDate && fromTime < toTime && !!address.trim() && !!mapQuery.trim() &&
    (when === 'now' || (!!scheduleYmd && scheduleYmd <= collectionDate));

  useEffect(() => setCount(null), [recipientText, items, collectionDate, fromTime, toTime, address, mapQuery, when, scheduleYmd]);

  const call = async (dryRun: boolean) => {
    setBusy(true);
    setNotice(null);
    try {
      const res: any = await httpsCallable(functions, 'whatsappMarketingBroadcast')({
        kind: 'collectionReady', audience: { type: 'selected', recipients: parsed.recipients },
        items, collectionDate, fromTime, toTime, address, mapQuery,
        scheduleYmd: when === 'schedule' ? scheduleYmd : undefined,
        consentConfirmed, dryRun,
      });
      if (dryRun) setCount(res.data as Record<string, number>);
      else if (res.data?.scheduled) {
        setNotice({ type: 'success', text: `Scheduled for 10 AM on ${scheduleYmd}.` });
        setCount(null);
      } else {
        const counts = (res.data?.counts || {}) as Record<string, number>;
        const skipped = Object.entries(counts).filter(([key]) => key !== 'sent').map(([key, value]) => `${key}: ${value}`).join(', ');
        setNotice({ type: 'success', text: `Sent to ${counts.sent || 0}.${skipped ? ` Skipped: ${skipped}.` : ''}` });
        setCount(null);
      }
    } catch (err: any) {
      setNotice({ type: 'error', text: String(err?.message || err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Send an item ready for collection">
      <Stack gap={1.25}>
        <Typography sx={{ fontSize: 12, color: UI.muted }}>One person per line: Name | mobile | language. Use en, gu or hi. Duplicate numbers are removed.</Typography>
        <TextField multiline minRows={4} label="Selected recipients" placeholder={'Amit Shah | 9876543210 | en\nરમીલાબેન | 9876543211 | gu'} value={recipientText} onChange={(e) => setRecipientText(e.target.value)} sx={denseInputSx} />
        {parsed.errors.length > 0 && <Alert severity="error" sx={{ fontSize: 12 }}>{parsed.errors.slice(0, 3).join(' ')}</Alert>}
        <Stack direction="row" gap={1} flexWrap="wrap">
          {AUTO_REPLY_LANGS.map(({ key, label }) => (
            <TextField key={key} size="small" label={`Item (${label})`} placeholder={key === 'en' ? 'Diwali gift' : key === 'gu' ? 'દિવાળી ભેટ' : 'दिवाली उपहार'} value={items[key]} onChange={(e) => setItems((current) => ({ ...current, [key]: e.target.value }))} sx={{ ...denseInputSx, flex: 1, minWidth: 170 }} />
          ))}
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <TextField size="small" type="date" label="Collection date" value={collectionDate} onChange={(e) => setCollectionDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ ...denseInputSx, minWidth: 180 }} />
          <TextField size="small" type="time" label="From (IST)" value={fromTime} onChange={(e) => setFromTime(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ ...denseInputSx, minWidth: 140 }} />
          <TextField size="small" type="time" label="Until (IST)" value={toTime} onChange={(e) => setToTime(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ ...denseInputSx, minWidth: 140 }} />
        </Stack>
        <TextField size="small" label="Full pickup address" value={address} onChange={(e) => setAddress(e.target.value)} sx={denseInputSx} />
        <TextField size="small" label="Map search or coordinates" helperText="Use a precise place name or latitude,longitude. The Get directions button searches this location in Google Maps." value={mapQuery} onChange={(e) => setMapQuery(e.target.value)} sx={denseInputSx} />
        {previewRecipient && (
          <Box sx={{ p: 1.5, bgcolor: UI.canvas, border: `1px solid ${UI.line}`, borderRadius: 1.5 }}>
            <Typography sx={{ fontSize: 11.5, color: UI.muted, mb: 0.75 }}>Preview for {previewRecipient.name} · {previewLang.toUpperCase()}</Typography>
            <Typography sx={{ fontSize: 12.5, whiteSpace: 'pre-line', color: UI.ink }}>{previewLines.join('\n\n')}</Typography>
            <Typography sx={{ fontSize: 12, color: UI.muted, mt: 1 }}>Get directions → {mapQuery ? `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}` : 'add a map search or coordinates'}</Typography>
          </Box>
        )}
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Button size="small" variant={when === 'now' ? 'contained' : 'outlined'} onClick={() => setWhen('now')} sx={when === 'now' ? primaryButtonSx : textButtonSx}>Send now</Button>
          <Button size="small" variant={when === 'schedule' ? 'contained' : 'outlined'} onClick={() => setWhen('schedule')} sx={when === 'schedule' ? primaryButtonSx : textButtonSx}>Schedule</Button>
          {when === 'schedule' && <TextField size="small" type="date" label="Send date (10 AM IST)" value={scheduleYmd} onChange={(e) => setScheduleYmd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ ...denseInputSx, minWidth: 190 }} />}
        </Stack>
        <FormControlLabel control={<Checkbox checked={consentConfirmed} onChange={(e) => setConsentConfirmed(e.target.checked)} size="small" />} label={<Typography sx={{ fontSize: 12 }}>These people agreed to receive WhatsApp messages from us.</Typography>} />
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Button size="small" variant="outlined" disabled={!ready || busy} onClick={() => call(true)} sx={textButtonSx}>Check recipients</Button>
          {count && <Typography sx={{ fontSize: 12, color: UI.muted }}>{count.recipients} selected · about {count.willSend} eligible</Typography>}
          {count && <Button size="small" variant="contained" disabled={!ready || !consentConfirmed || !count.willSend || busy} onClick={() => call(false)} sx={primaryButtonSx}>{busy ? 'Working…' : when === 'schedule' ? 'Schedule collection notice' : `Send to ${count.willSend}`}</Button>}
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: UI.muted }}>Uses a Meta-approved marketing template. The selected recipient list, item, date, time and location are saved when scheduled. Standard marketing limits and opt-outs apply.</Typography>
        {notice && <Alert severity={notice.type} sx={{ fontSize: 12 }}>{notice.text}</Alert>}
        <ScheduledList kind="collectionReady" />
      </Stack>
    </Panel>
  );
};

const ScheduledList = ({ kind }: { kind: 'newLaunch' | 'festival' | 'collectionReady' }) => {
  const [rows, setRows] = useState<Array<{ id: string; data: any }>>([]);
  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'whatsappScheduledBroadcasts'), where('kind', '==', kind)),
        (snap) =>
          setRows(
            snap.docs
              .map((d) => ({ id: d.id, data: d.data() }))
              .filter((r) => r.data.status !== 'cancelled')
              .sort((a, b) => (b.data.sendAt?.toMillis?.() || 0) - (a.data.sendAt?.toMillis?.() || 0))
              .slice(0, 8)
          ),
        () => setRows([])
      ),
    [kind]
  );
  if (!rows.length) return null;
  return (
    <Box sx={{ borderTop: `1px dashed ${UI.line}`, pt: 1 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: UI.ink, mb: 0.5 }}>Scheduled and recent</Typography>
      {rows.map((r) => (
        <Stack key={r.id} direction="row" alignItems="center" gap={1} sx={{ py: 0.4 }}>
          <Typography sx={{ fontSize: 12.5, color: UI.ink, flex: 1, minWidth: 0 }}>
            {r.data.sendAt?.toDate?.().toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })} ·{' '}
            {kind === 'newLaunch' ? r.data.launchName || 'Launch' : kind === 'festival' ? r.data.festival || 'Festival' : r.data.items?.en || 'Collection'} · {r.data.status}
            {r.data.result?.counts?.sent ? ` · sent ${r.data.result.counts.sent}` : ''}
            {r.data.error ? ` · ${r.data.error}` : ''}
          </Typography>
          {r.data.status === 'scheduled' && (
            <Button size="small" onClick={() => updateDoc(doc(db, 'whatsappScheduledBroadcasts', r.id), { status: 'cancelled' })} sx={{ ...textButtonSx, color: '#a12a22' }}>
              Cancel
            </Button>
          )}
        </Stack>
      ))}
    </Box>
  );
};

const OldBirthdayWarning = () => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    getDoc(doc(db, 'birthdayWishConfig', 'main'))
      .then((snap) => setEnabled((snap.data() as any)?.enabled === true))
      .catch(() => setEnabled(false));
  }, []);
  return enabled ? (
    <Alert severity="warning" sx={{ mt: 2, fontSize: 12.5, py: 0 }}>
      The older Birthday wishes setting is still on (Settings → Birthday wishes). Turn it off before switching this on, or people get two wishes.
    </Alert>
  ) : null;
};

const HubJourneyTools = ({ tool }: { tool?: Tool }) => {
  if (tool === 'launchComposer') return <MarketingComposer kind="newLaunch" />;
  if (tool === 'festivalComposer') return <MarketingComposer kind="festival" />;
  if (tool === 'collectionComposer') return <CollectionComposer />;
  if (tool === 'birthdayInfo') {
    return (
      <>
        <OldBirthdayWarning />
        <Typography sx={{ fontSize: 12.5, color: UI.muted, mt: 2 }}>
          Uses the date of birth saved on booking customers and brokers. Staff birthdays are not included.
        </Typography>
      </>
    );
  }
  if (tool === 'coldLeadInfo') {
    return (
      <Typography sx={{ fontSize: 12.5, color: UI.muted, mt: 2 }}>
        Runs every day at 11 AM. Picks open leads whose last activity was 7 to 30 days ago, oldest first, up to 50 a day.
      </Typography>
    );
  }
  if (tool === 'constructionComposer') return <BroadcastComposer kind="construction" />;
  if (tool === 'possessionComposer') return <BroadcastComposer kind="possession" />;
  if (tool === 'documentsChecklist') return <DocumentsChecklistTool />;
  if (tool === 'priceInDrawer') {
    return (
      <Typography sx={{ fontSize: 12.5, color: UI.muted, mt: 2 }}>
        Executives send this from the lead drawer: the price tag icon next to the phone number.
      </Typography>
    );
  }
  if (tool === 'demandDueDate') {
    return (
      <Typography sx={{ fontSize: 12.5, color: UI.muted, mt: 2 }}>
        Set a <b>Payment Due Date</b> when generating a demand letter in Office → Units → booking workflow. Without a due date no reminder is sent.
      </Typography>
    );
  }
  return null;
};

export default HubJourneyTools;
